import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { Loader2 } from "lucide-react"
import { useAuth } from "./AuthProvider"
import { supabase } from "../lib/supabase"
import { isDemoSite } from "../lib/isDemoSite"
import { NewActivityModal } from "./NewActivityModal"
import { ReportDetailModal, type ReportForDetailModal } from "./ReportDetailModal"

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

async function fetchReportForModal(reportId: string): Promise<ReportForDetailModal | null> {
  const { data } = await (supabase as any)
    .from("activities")
    .select("*")
    .eq("id", reportId)
    .single()
  if (!data) return null
  let propertyName: string | undefined
  if (data.property_id) {
    const { data: prop } = await supabase
      .from("properties")
      .select("name")
      .eq("id", data.property_id)
      .single()
    propertyName = prop?.name
  }
  return {
    id: data.id,
    title: data.title,
    status: data.status ?? undefined,
    service_type: data.service_type ?? undefined,
    activity_date: data.activity_date,
    checklist_notes: data.checklist_notes ?? undefined,
    photos: data.photos ?? [],
    checklist: (data.checklist ?? undefined) as Record<string, { tasks: { task: string; completed: boolean }[] }> | undefined,
    property: propertyName ? { name: propertyName } : undefined,
    operator_name: data.operator_name ?? undefined,
    supply_snap_photo: data.supply_snap_photo ?? undefined,
    supply_snap_estimate: data.supply_snap_estimate ?? undefined,
    supply_snap_room: data.supply_snap_room ?? undefined,
    site_sense_audio_url: data.site_sense_audio_url ?? undefined,
    gemini_analysis: (data.gemini_analysis ?? undefined) as import("../lib/geminiSupplyAnalysisSchema").GeminiSupplyAnalysis | null | undefined,
    analysis_status: (data.analysis_status ?? undefined) as "pending" | "completed" | "failed" | null | undefined,
  }
}

/**
 * Renders report modals based on URL search params.
 * ?report=new → New report modal (operator or admin by role).
 * ?report=:id → View/edit report modal.
 * All report create/edit/view flows stay as modals; no full-page report routes.
 */
export function GlobalReportModals() {
  const location = useLocation()
  const navigate = useNavigate()
  const { role, isCreator, canWrite } = useAuth()
  const params = new URLSearchParams(location.search)
  const reportParam = params.get("activity") ?? params.get("report")

  const isNew = reportParam === "new"
  const reportId = reportParam && reportParam !== "new" && UUID_REGEX.test(reportParam) ? reportParam : null

  const [viewReport, setViewReport] = useState<ReportForDetailModal | null>(null)
  const [viewLoading, setViewLoading] = useState(false)
  const [viewError, setViewError] = useState(false)

  useEffect(() => {
    if (!reportId) {
      setViewReport(null)
      setViewError(false)
      return
    }
    let cancelled = false
    setViewLoading(true)
    setViewError(false)
    fetchReportForModal(reportId)
      .then((report) => {
        if (!cancelled) {
          setViewReport(report)
          if (!report) setViewError(true)
        }
      })
      .catch(() => {
        if (!cancelled) setViewError(true)
      })
      .finally(() => {
        if (!cancelled) setViewLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [reportId])

  const clearReportParam = () => {
    const p = new URLSearchParams(location.search)
    p.delete("activity")
    p.delete("report")
    p.delete("property")
    const search = p.toString()
    navigate({ pathname: location.pathname, search: search ? `?${search}` : "" }, { replace: true })
  }

  const demo = isDemoSite()
  const blockForDemo = (demo && !isCreator) || !canWrite

  if (isNew && !blockForDemo) {
    const mode = (role === "admin" || role === "creator") ? "admin" : "operator"
    return (
      <NewActivityModal
        open
        onClose={clearReportParam}
        mode={mode}
        onSuccess={clearReportParam}
      />
    )
  }

  if (isNew && blockForDemo) {
    clearReportParam()
    return null
  }

  if (reportId) {
    if (viewLoading) {
      return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center glass-backdrop-md">
          <Loader2 className="w-10 h-10 animate-spin text-white" />
        </div>
      )
    }
    if (viewError || !viewReport) {
      return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 glass-backdrop-md" onClick={clearReportParam} aria-hidden />
          <div className="relative rounded-2xl bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-700 p-6 max-w-sm text-center">
            <p className="text-stone-600 dark:text-stone-400 mb-4">
              Report not found or you don’t have access.
            </p>
            <button type="button" onClick={clearReportParam} className="btn btn-primary">
              Close
            </button>
          </div>
        </div>
      )
    }
    return (
      <ReportDetailModal
        report={viewReport}
        onClose={clearReportParam}
        onEdit={(role === "admin" || role === "creator") && !blockForDemo ? () => {} : undefined}
        onReportUpdated={!blockForDemo ? () => {} : undefined}
        isAdmin={role === "admin" || role === "creator"}
        onDeleteReport={
          (role === "admin" || role === "creator") && !blockForDemo && viewReport.id
            ? async (id) => {
                const { error } = await supabase.from("activities").delete().eq("id", id)
                if (error) throw error
                clearReportParam()
              }
            : undefined
        }
      />
    )
  }

  return null
}
